import { Box } from "@outschool/backpack";
import {
  DefaultVariants,
  ExperimentVariants,
} from "@outschool/experiments-shared";
import { useSession } from "@outschool/ui-auth";
import { useOnScreen, usePreviousValue } from "@outschool/ui-utils";
import React, { useEffect, useRef } from "react";

import useExperiment from "../../hooks/useExperiment";
import { NoInfer } from "./NoInfer";

interface ExperimentTriggerWrapperProps<
  Variants extends ExperimentVariants = DefaultVariants,
> {
  /* The name of the experiment to trigger, should match the name in experiments admin. */
  name: string;
  /* Default variant to use if the experiment is not assigned. */
  defaultVariant?: NoInfer<Variants>;
  /* Child nodes to wrap. */
  children: React.ReactNode;
  /* Whether to disable triggering the experiment when the component is in the viewport. */
  disable?: boolean;
  /* When this flag is true, only one trigger will be sent for a given experiment name per page-load. That is,
  if there's a list of <Experiment name="exp1" triggerOnce/> components, the first one to be in the viewport will trigger the experiment
  and the rest will not. */
  triggerOnce?: boolean;
}

/**
 * Global map to track if an experiment has been triggered by experiment name, to support the triggerOnce flag.
 */
let hasTriggeredMap: Record<string, boolean> = {};

/**
 * Wraps a component used in the given experiment and triggers that experiment when that component enters the viewport.
 */
export default function ExperimentTriggerWrapper<
  Variants extends ExperimentVariants = DefaultVariants,
>({
  name,
  defaultVariant,
  disable = false,
  triggerOnce = true,
  children,
}: ExperimentTriggerWrapperProps<Variants>) {
  const { trigger } = useExperiment<Variants>(name, defaultVariant);
  const { currentUser, currentUserHasLoaded } = useSession();
  const prevCurrentUser = usePreviousValue(currentUser);

  const nodeRef = useRef<HTMLDivElement>(null);
  // This seems redundant with the hasTriggeredMap, but it's necessary because we only want each
  // ExperimentTriggerWrapper component to trigger once. The triggerOnce flag handles the global tracking
  // between different components rendering different parts of an experiment UI.
  const hasTriggeredRef = useRef(false);

  const isOnScreen = useOnScreen(nodeRef.current);

  useEffect(() => {
    const blockTrigger =
      !currentUserHasLoaded ||
      disable ||
      hasTriggeredRef.current ||
      (triggerOnce && hasTriggeredMap[name]);

    if (isOnScreen && !blockTrigger) {
      trigger();
      hasTriggeredRef.current = true;
      hasTriggeredMap[name] = true;
    }
  }, [
    isOnScreen,
    trigger,
    disable,
    name,
    triggerOnce,
    currentUserHasLoaded,
    children,
  ]);

  // Reset the hasTriggeredMap when the current user changes, to ensure that the experiment can trigger for the new user login state (potential new variant assignment).
  useEffect(() => {
    if (prevCurrentUser?.uid !== currentUser?.uid) {
      hasTriggeredMap = {};
    }
  }, [currentUser, prevCurrentUser]);

  return (
    // Need to wrap in a Box in order to correctly observe the component for useOnScreen.
    // Note: we may be able to remove this and use a cloning approach once we upgrade to React 19
    // See https://gitlab.com/outschool-eng/outschool/-/merge_requests/33366#note_2981683919
    <Box
      ref={nodeRef}
      sx={{
        ...(children === null
          ? { height: "1px", width: "1px", position: "absolute" }
          : {}),
      }}
    >
      {children}
    </Box>
  );
}
